import authAxios from 'src/modules/shared/axios/authAxios';
import UserService from 'src/modules/user/userService';
import EntitlementService from 'src/modules/entitlement/entitlementService';

const userCache = new Map<string, any>();
const entitlementCache = new Map<string, any>();

async function resolveUserRelation(value: any) {
  if (!value) return value;
  if (typeof value === 'object' && value.id) return value;
  if (typeof value !== 'string') return value;

  const key = value.trim();
  if (!key) return null;
  if (userCache.has(key)) return userCache.get(key);

  const results = await UserService.fetchUserAutocomplete(
    key,
    10,
  );

  const normalizedKey = key.toLowerCase();
  const match =
    results?.find?.((r) =>
      String(r?.label || '')
        .toLowerCase()
        .includes(normalizedKey),
    ) || results?.[0];

  const resolved = match?.id ? { id: match.id, label: match.label } : null;
  userCache.set(key, resolved);
  return resolved;
}

async function resolveEntitlementRelation(value: any) {
  if (!value) return value;
  if (typeof value === 'object' && value.id) return value;
  if (typeof value !== 'string') return value;

  const key = value.trim();
  if (!key) return null;
  if (entitlementCache.has(key)) return entitlementCache.get(key);

  const results = await EntitlementService.listAutocomplete(
    key,
    undefined,
    20,
  );

  const normalizedKey = key.toLowerCase();
  const exact =
    results?.find?.(
      (r) =>
        String(r?.label || '').toLowerCase() ===
        normalizedKey,
    ) || null;

  const match = exact || results?.[0];
  const resolved = match?.id ? { id: match.id, label: match.label } : null;
  entitlementCache.set(key, resolved);
  return resolved;
}

export default class UserEntitlementService {
  static async update(id, data) {
    const body = {
      id,
      data,
    };

    const response = await authAxios.put(
      `/user-entitlement/${id}`,
      body,
    );

    return response.data;
  }

  static async destroyAll(ids) {
    const params = {
      ids,
    };

    const response = await authAxios.delete(
      `/user-entitlement`,
      {
        params,
      },
    );

    return response.data;
  }

  static async deactivate(id) {
    const response = await authAxios.delete(
      `/user-entitlement/${id}/deactivate`,
    );

    return response.data;
  }

  static async unarchiveAll(ids) {
    const body = {
      ids,
    };

    const response = await authAxios.put(
      `/user-entitlement/unarchive`,
      body,
    );

    return response.data;
  }

  static async create(data) {
    const body = {
      data,
    };

    const response = await authAxios.post(
      `/user-entitlement`,
      body,
    );

    return response.data;
  }

  static async import(values, importHash) {
    const data = {
      ...values,
    };

    if (typeof data.active !== 'boolean') {
      data.active = true;
    }

    data.user = await resolveUserRelation(data.user);
    data.entitlement = await resolveEntitlementRelation(
      data.entitlement,
    );

    const body = {
      data,
      importHash,
    };

    const response = await authAxios.post(
      `/user-entitlement/import`,
      body,
    );

    return response.data;
  }

  static async find(id) {
    const response = await authAxios.get(
      `/user-entitlement/${id}`,
    );

    return response.data;
  }

  static async list(filter, orderBy, limit, offset) {
    const params = {
      filter,
      orderBy,
      limit,
      offset,
    };

    const response = await authAxios.get(
      `/user-entitlement`,
      {
        params,
      },
    );

    return response.data;
  }

  static async listAutocomplete(query, limit) {
    const params = {
      query,
      limit,
    };

    const response = await authAxios.get(
      `/user-entitlement/autocomplete`,
      {
        params,
      },
    );

    return response.data;
  }
}
